import { useState, useEffect, useRef } from "react";
import { Input } from "@/components/ui/input";
import { api } from "@/lib/api";
import { MapPin, Loader2 } from "lucide-react";

/**
 * Address autocomplete using OSM Nominatim via backend proxy.
 * onSelect(address, lat, lng)
 */
export default function AddressAutocomplete({ value, onChange, onSelect, placeholder, required, testId }) {
  const [results, setResults] = useState([]);
  const [show, setShow] = useState(false);
  const [loading, setLoading] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);
  const timerRef = useRef(null);
  const wrapperRef = useRef(null);

  useEffect(() => {
    if (!value || value.length < 3) { setResults([]); setHasSearched(false); return; }
    clearTimeout(timerRef.current);
    timerRef.current = setTimeout(async () => {
      setLoading(true);
      try {
        const { data } = await api.get("/geocode", { params: { q: value } });
        setResults(data);
      } catch (e) {
        console.warn("Geocode failed:", e);
        setResults([]);
      }
      setLoading(false);
      setHasSearched(true);
    }, 400);
    return () => clearTimeout(timerRef.current);
  }, [value]);

  useEffect(() => {
    const onClick = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) setShow(false);
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  return (
    <div className="relative" ref={wrapperRef}>
      <div className="relative">
        <Input
          value={value}
          onChange={(e) => { onChange(e.target.value); setShow(true); }}
          onFocus={() => setShow(true)}
          placeholder={placeholder}
          required={required}
          data-testid={testId}
          autoComplete="off"
        />
        {loading && <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 animate-spin text-muted-foreground" />}
      </div>
      {show && (results.length > 0 || (hasSearched && !loading && value.length >= 3)) && (
        <div className="absolute z-50 left-0 right-0 mt-1 bg-popover border border-border rounded-xl shadow-lg max-h-72 overflow-y-auto">
          {results.length === 0 ? (
            <div className="px-3 py-3 text-xs text-muted-foreground">
              Ingen forslag fundet. Du kan skrive adressen manuelt.
            </div>
          ) : results.map((r, i) => (
            <button
              key={i}
              type="button"
              onClick={() => {
                onSelect(r.display_name, r.lat, r.lng);
                setShow(false);
              }}
              className="w-full text-left px-3 py-2 hover:bg-muted transition-colors flex items-start gap-2 border-b border-border last:border-0"
            >
              <MapPin className="w-4 h-4 text-muted-foreground shrink-0 mt-0.5" />
              <span className="text-sm">{r.display_name}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
